import { logger } from '@/lib/logger';

import {
  type ChatProgressData,
  ChatProgressRateLimitedError,
  type ChatRunPollClient,
  classifyChatProgress,
  createDefaultPollClient,
} from './chatProgressPoll';
import {
  buildCtx,
  callHandlerSafely,
  type Entry,
  MAX_TRACKERS,
  MAX_WALL_MS,
  pickEvictableTracker,
  POLL_INTERVAL_MS,
} from './chatRunEntry';
import { DEFAULT_CHAT_RUN_HANDLERS } from './defaultChatRunHandlers';

export { MAX_TRACKERS };

const runs = new Map<string, Entry>();
const visibleKeys = new Set<string>();
const listeners = new Set<() => void>();

let pollClientFactory = createDefaultPollClient;

function emitChange(): void {
  listeners.forEach(l => l());
}

export function subscribeChatRuns(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function isRunActive(key: string): boolean {
  const e = runs.get(key);
  return !!e && !e.resolved;
}
export function getActiveRunCount(): number {
  return runs.size;
}

// suppressToast is read synchronously by buildCtx() at handler-dispatch time
// — a visibility toggle can only affect the *next* call, never an in-flight one.
export function setRunVisible(key: string): void {
  visibleKeys.add(key);
  const e = runs.get(key);
  if (e) {
    e.suppressToast = true;
  }
}

export function setRunHidden(key: string): void {
  visibleKeys.delete(key);
  const e = runs.get(key);
  if (e) {
    e.suppressToast = false;
  }
}

type RegisterChatRunArgs = Pick<
  Entry,
  'key' | 'orgId' | 'conversationId' | 'taskId' | 'handlers'
> & { treatPausedAsComplete?: boolean };

export function registerChatRun({
  key,
  orgId,
  conversationId,
  taskId,
  handlers,
  treatPausedAsComplete = false,
}: RegisterChatRunArgs): void {
  const existing = runs.get(key);
  if (existing && !existing.resolved && existing.taskId === taskId) {
    return;
  }
  if (existing && !existing.resolved && existing.taskId !== taskId) {
    logger.debug('[chatRunTracker] superseding run', {
      component: 'reasoning',
      key,
      oldTaskId: existing.taskId,
      newTaskId: taskId,
    });
    void callHandlerSafely('onSuperseded', { key }, () =>
      existing.handlers.onSuperseded?.({
        key,
        oldTaskId: existing.taskId,
        newTaskId: taskId,
      })
    );
    cleanup(existing);
    runs.delete(key);
  } else if (!existing && runs.size >= MAX_TRACKERS) {
    // At capacity for a new key — evict the least-useful run, don't skip it.
    const victim = pickEvictableTracker(runs, visibleKeys);
    if (victim) {
      logger.warn(
        '[chatRunTracker] max trackers reached — evicting oldest to make room',
        {
          component: 'reasoning',
          key,
          evictedKey: victim.key,
          maxTrackers: MAX_TRACKERS,
        }
      );
      void callHandlerSafely('onEvicted', { key }, () =>
        handlers.onEvicted?.({ key, evictedKey: victim.key })
      );
      cleanup(victim);
      runs.delete(victim.key);
      emitChange();
    }
  }

  const entry: Entry = {
    key,
    orgId,
    conversationId,
    taskId,
    handlers,
    pollTimer: null,
    watchdogTimer: null,
    pollCount: 0,
    consecutiveFailures: 0,
    startedAt: Date.now(),
    resolved: false,
    suppressToast: visibleKeys.has(key),
    treatPausedAsComplete,
  };
  runs.set(key, entry);
  entry.watchdogTimer = setTimeout(
    () => onWatchTimeout(key, taskId),
    MAX_WALL_MS
  );
  emitChange();
  startPolling(key);
}

function startPolling(key: string): void {
  const entry = runs.get(key);
  if (!entry || entry.resolved) {
    return;
  }
  const poll = async () => {
    const e = runs.get(key);
    if (!e || e.resolved) {
      return;
    }
    if (Date.now() - e.startedAt >= MAX_WALL_MS) {
      onWatchTimeout(key, e.taskId);
      return;
    }
    // Telemetry only — the wall-clock watchdog is the sole termination ceiling.
    e.pollCount++;

    const dispatchedTaskId = e.taskId;
    let rateLimitRetryAfterMs: number | undefined;
    try {
      const data: ChatProgressData = await pollClientFactory(
        e.orgId
      ).fetchChatProgress(e.conversationId, dispatchedTaskId);
      const current = runs.get(key);
      // Identity check (`current === e`) guards against a same-key/taskId
      // replacement finalizing a stale poll.
      if (
        !current ||
        current !== e ||
        current.resolved ||
        current.taskId !== dispatchedTaskId
      ) {
        return;
      }
      current.consecutiveFailures = 0;
      const outcome = classifyChatProgress(data, {
        treatPausedAsComplete: current.treatPausedAsComplete,
      });
      if (outcome === 'failed') {
        failRun(
          key,
          dispatchedTaskId,
          'failed',
          data.result?.error ?? undefined
        );
        return;
      }
      if (outcome === 'complete') {
        void finalizeRun(key, dispatchedTaskId);
        return;
      }
    } catch (err) {
      // Gate on object identity (`failed === e`), not taskId alone, so a
      // stale rejection from a superseded/evicted run can't land on a
      // replacement that reused the same key+taskId.
      const failed = runs.get(key);
      if (
        !failed ||
        failed !== e ||
        failed.resolved ||
        failed.taskId !== dispatchedTaskId
      ) {
        return;
      }
      failed.consecutiveFailures++;
      void callHandlerSafely('onPollFailed', { key }, () =>
        failed.handlers.onPollFailed?.({
          key,
          conversationId: failed.conversationId,
          error: err,
        })
      );
      logger.warn('[chatRunTracker] poll failed, will retry until watchdog', {
        component: 'reasoning',
        conversationId: failed.conversationId,
        key,
        consecutiveFailures: failed.consecutiveFailures,
        error: err,
      });
      if (
        err instanceof ChatProgressRateLimitedError &&
        err.retryAfterMs !== undefined
      ) {
        rateLimitRetryAfterMs = Math.max(err.retryAfterMs, POLL_INTERVAL_MS);
      }
    }
    const c2 = runs.get(key);
    if (c2 && c2 === e && !c2.resolved && c2.taskId === dispatchedTaskId) {
      // Backoff only applies after a failure (0 on success collapses to
      // POLL_INTERVAL_MS); a rate-limited response overrides both via
      // Retry-After (never less than POLL_INTERVAL_MS).
      const delay =
        rateLimitRetryAfterMs ??
        Math.min(
          POLL_INTERVAL_MS * 2 ** Math.max(c2.consecutiveFailures - 1, 0),
          30_000
        );
      c2.pollTimer = setTimeout(poll, delay);
    }
  };
  void poll();
}

export function onChatRunUpdatedWs(key: string, taskId: string): void {
  const e = runs.get(key);
  if (!e || e.resolved || e.taskId !== taskId) {
    return;
  }
  void finalizeRun(key, taskId);
}
function cleanup(entry: Entry): void {
  if (entry.pollTimer) {
    clearTimeout(entry.pollTimer);
  }
  if (entry.watchdogTimer) {
    clearTimeout(entry.watchdogTimer);
  }
}
function resolveEntry(key: string, taskId: string): Entry | undefined {
  const e = runs.get(key);
  if (!e || e.resolved || e.taskId !== taskId) {
    return undefined;
  }
  e.resolved = true;
  cleanup(e);
  runs.delete(key);
  emitChange();
  return e;
}
async function finalizeRun(key: string, taskId: string): Promise<void> {
  const e = resolveEntry(key, taskId);
  if (!e) {
    return;
  }
  await callHandlerSafely('onComplete', { key }, () =>
    e.handlers.onComplete?.(buildCtx(e, key, taskId))
  );
}

function failRun(
  key: string,
  taskId: string,
  reason: 'failed' | 'timeout',
  serverMessage?: string
): void {
  const e = resolveEntry(key, taskId);
  if (!e) {
    return;
  }
  void callHandlerSafely('onFailed', { key }, () =>
    e.handlers.onFailed?.(buildCtx(e, key, taskId), reason, serverMessage)
  );
}
function onWatchTimeout(key: string, taskId: string): void {
  const { conversationId, pollCount } = runs.get(key) ?? {};
  logger.error(
    '[chatRunTracker] chat run watch timed out',
    new Error('chat watch ceiling reached'),
    { component: 'reasoning', conversationId, pollCount }
  );
  failRun(key, taskId, 'timeout');
}

type TrackChatRunArgs = Pick<Entry, 'orgId' | 'conversationId' | 'taskId'>;

export function trackChatRun({
  orgId,
  conversationId,
  taskId,
}: TrackChatRunArgs): void {
  registerChatRun({
    key: conversationId,
    orgId,
    conversationId,
    taskId,
    handlers: DEFAULT_CHAT_RUN_HANDLERS,
  });
}

export function setConversationVisible(conversationId: string): void {
  setRunVisible(conversationId);
}
export function setConversationHidden(conversationId: string): void {
  setRunHidden(conversationId);
}
export function isChatRunActive(conversationId: string): boolean {
  return isRunActive(conversationId);
}

export function __setPollClientForTesting(
  fn: (orgId: string) => ChatRunPollClient
): void {
  pollClientFactory = fn;
}

export function __resetChatRunTrackerForTesting(): void {
  runs.forEach(e => cleanup(e));
  runs.clear();
  visibleKeys.clear();
  listeners.clear();
  pollClientFactory = createDefaultPollClient;
}
